import gql from "graphql-tag";
import { get } from "lodash";
import type { ApolloClient } from "@apollo/client";

import type {
  AddContributionMutation,
  AddContributionMutationVariables,
  AddOnboardingPayCheckContributionMutation,
  AddOnboardingPayCheckContributionMutationVariables,
  AddOnboardingRecurringContributionMutation,
  AddOnboardingRecurringContributionMutationVariables,
  AddPayCheckContributionMutation,
  AddPayCheckContributionMutationVariables,
  AddRecurringContributionMutation,
  AddRecurringContributionMutationVariables,
  EditPayCheckContributionMutation,
  EditPayCheckContributionMutationVariables,
  GetContributionAbilityAndEstimatesQuery,
  GetContributionAbilityAndEstimatesQueryVariables,
  GetContributionAbilityQuery,
  GetDailyToRecurringAmountQuery,
  GetDailyToRecurringAmountQueryVariables,
  GetRecommendedContributionAmountsQuery,
  GetRecommendedContributionAmountsQueryVariables,
  GetRecurringContributionsQuery,
} from "types/graphql";
import type {
  AddPaycheckSplitContributionVariables,
  EditPaycheckSplitContributionVariables,
  PaychexSplitContribution,
} from "types/models/paychexWorkerRecord";
import { contributionTypes } from "statics/contributionTypes";

// link bank with Icon
export function addContribution({
  client,
  iraAccountId,
  amount,
  bankId,
  applyToPriorYear = false,
}: {
  client: ApolloClient<object>;
  iraAccountId: string;
  amount: number;
  bankId: string;
  applyToPriorYear?: boolean;
}): Promise<AddContributionMutation> {
  return new Promise((resolve, reject) => {
    const ADD_CONTRIBUTION = gql`
      mutation addContribution(
        $contributionType: String
        $amount: Float!
        $bankId: ID!
        $iraAccountId: ID!
        $applyToPriorYear: Boolean
      ) {
        addContribution(
          contributionType: $contributionType
          amount: $amount
          bankId: $bankId
          iraAccountId: $iraAccountId
          applyToPriorYear: $applyToPriorYear
        )
      }
    `;

    const result = client.mutate<
      AddContributionMutation,
      AddContributionMutationVariables
    >({
      mutation: ADD_CONTRIBUTION,
      variables: {
        contributionType: "REGULAR",
        amount,
        bankId,
        iraAccountId,
        applyToPriorYear,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function addPaycheckContribution({
  client,
  amount,
  groupId,
  isDailyCustom,
  accountId,
}: {
  client: ApolloClient<object>;
  amount: number;
  groupId: string;
  isDailyCustom: boolean;
  accountId: string;
}): Promise<AddPayCheckContributionMutation["addPayCheckContribution"]> {
  return new Promise((resolve, reject) => {
    const ADD_CONTRIBUTION = gql`
      mutation addPayCheckContribution(
        $contributionType: String
        $amount: Float!
        $groupId: ID!
        $isDailyCustom: Boolean!
        $accountId: ID!
      ) {
        addPayCheckContribution(
          contributionType: $contributionType
          amount: $amount
          groupId: $groupId
          isDailyCustom: $isDailyCustom
          accountId: $accountId
        ) {
          id
          status
          company {
            id
            name
            planId
          }
          group {
            id
            payrollFrequency
            companyGroupIndex
          }
          schedule {
            start
            end
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
          }
          contribution {
            amount
            id
            contributionChangeRequests {
              userCurrentContributionAmount
              userNewContributionAmount
              status
              id
            }
          }
        }
      }
    `;

    const result = client.mutate<
      AddPayCheckContributionMutation,
      AddPayCheckContributionMutationVariables
    >({
      mutation: ADD_CONTRIBUTION,
      variables: {
        contributionType: "EMPLOYER",
        groupId,
        isDailyCustom,
        amount,
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addPayCheckContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function editPaycheckContribution(
  client: ApolloClient<object>,
  {
    amount,
    contributionId,
    isDailyCustom,
    accountId,
  }: {
    amount: number;
    contributionId: string;
    isDailyCustom: boolean;
    accountId: string;
  }
): Promise<EditPayCheckContributionMutation["editPayCheckContribution"]> {
  return new Promise((resolve, reject) => {
    const ADD_CONTRIBUTION = gql`
      mutation editPayCheckContribution(
        $id: ID!
        $amount: Float!
        $isDailyCustom: Boolean!
        $accountId: ID!
      ) {
        editPayCheckContribution(
          id: $id
          amount: $amount
          isDailyCustom: $isDailyCustom
          accountId: $accountId
        ) {
          id
          company {
            id
            name
            planId
          }
          group {
            id
            payrollFrequency
            companyGroupIndex
          }
          groupWithSchedule {
            group {
              id
              name
              payrollFrequency
            }
          }
          schedule {
            start
            end
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
          }
          contribution {
            amount
            id
          }
        }
      }
    `;

    const result = client.mutate<
      EditPayCheckContributionMutation,
      EditPayCheckContributionMutationVariables
    >({
      mutation: ADD_CONTRIBUTION,
      variables: {
        id: contributionId,
        amount,
        isDailyCustom,
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.editPayCheckContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function submitOnboardingPayrollContribution(
  client: ApolloClient<object>,
  {
    isDailyCustom,
    amount,
    groupId,
    accountId,
  }: AddOnboardingPayCheckContributionMutationVariables
): Promise<
  AddOnboardingPayCheckContributionMutation["addOnboardingPayCheckContribution"]
> {
  return new Promise((resolve, reject) => {
    const SUBMIT_ONBOARDING_CONTRIBUTION = gql`
      mutation addOnboardingPayCheckContribution(
        $groupId: ID!
        $amount: Float!
        $isDailyCustom: Boolean!
        $accountId: ID!
      ) {
        addOnboardingPayCheckContribution(
          groupId: $groupId
          amount: $amount
          isDailyCustom: $isDailyCustom
          accountId: $accountId
        )
      }
    `;

    const result = client.mutate<
      AddOnboardingPayCheckContributionMutation,
      AddOnboardingPayCheckContributionMutationVariables
    >({
      mutation: SUBMIT_ONBOARDING_CONTRIBUTION,
      variables: {
        isDailyCustom,
        amount,
        groupId,
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addOnboardingPayCheckContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function addOnboardingRecurringContribution(
  client: ApolloClient<object>,
  {
    accountId,
    amount,
    bankId,
    dayOne,
    frequency,
    anchorPayDate,
    isDailyCustom,
  }: AddOnboardingRecurringContributionMutationVariables
): Promise<
  AddOnboardingRecurringContributionMutation["addOnboardingRecurringContribution"]
> {
  return new Promise((resolve, reject) => {
    const SUBMIT_ONBOARDING_CONTRIBUTION = gql`
      mutation addOnboardingRecurringContribution(
        $amount: Float!
        $accountId: ID!
        $bankId: ID!
        $dayOne: Int
        $frequency: String!
        $anchorPayDate: Date!
        $isDailyCustom: Boolean
      ) {
        addOnboardingRecurringContribution(
          amount: $amount
          accountId: $accountId
          bankId: $bankId
          dayOne: $dayOne
          frequency: $frequency
          anchorPayDate: $anchorPayDate
          isDailyCustom: $isDailyCustom
        )
      }
    `;

    const result = client.mutate<
      AddOnboardingRecurringContributionMutation,
      AddOnboardingRecurringContributionMutationVariables
    >({
      mutation: SUBMIT_ONBOARDING_CONTRIBUTION,
      variables: {
        amount,
        accountId,
        bankId,
        dayOne,
        frequency,
        anchorPayDate,
        isDailyCustom,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addOnboardingRecurringContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function addRecurringContribution(
  client: ApolloClient<object>,
  {
    accountId,
    amount,
    bankId,
    dayOne,
    frequency,
    anchorPayDate,
    isDailyCustom,
    scheduleId,
  }: AddRecurringContributionMutationVariables
): Promise<AddRecurringContributionMutation["addRecurringContribution"]> {
  return new Promise((resolve, reject) => {
    const SUBMIT_ONBOARDING_CONTRIBUTION = gql`
      mutation addRecurringContribution(
        $amount: Float!
        $accountId: ID!
        $bankId: ID!
        $dayOne: Int
        $frequency: String!
        $anchorPayDate: Date!
        $isDailyCustom: Boolean
        $scheduleId: ID
      ) {
        addRecurringContribution(
          amount: $amount
          accountId: $accountId
          bankId: $bankId
          dayOne: $dayOne
          frequency: $frequency
          anchorPayDate: $anchorPayDate
          isDailyCustom: $isDailyCustom
          scheduleId: $scheduleId
        ) {
          frequency
          anchorPayDate
          amount
          dayOne
          dayTwo
          sourceBankAccountId
          targetIraAccountId
          nextScheduledDate
          scheduleId
        }
      }
    `;

    const result = client.mutate<
      AddRecurringContributionMutation,
      AddRecurringContributionMutationVariables
    >({
      mutation: SUBMIT_ONBOARDING_CONTRIBUTION,
      variables: {
        amount,
        accountId,
        bankId,
        dayOne,
        frequency,
        anchorPayDate,
        isDailyCustom,
        scheduleId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addRecurringContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// `payrollFrequency` is a Paychex-Perks addition (backend-pending): when
// present, the backend builds recommendations for that pay frequency (taken
// from the selected worker record's payroll schedule) instead of an employer
// group or self-employed default. The codegen variables type predates it, so
// widen the param locally.
type RecommendationVariables =
  GetRecommendedContributionAmountsQueryVariables & {
    payrollFrequency?: string | null;
  };

export function getRecommendedContributionAmounts(
  client: ApolloClient<object>,
  { groupId, isSelfEmployed, payrollFrequency }: RecommendationVariables
): Promise<
  GetRecommendedContributionAmountsQuery["getRecommendedContributionAmounts"]
> {
  return new Promise((resolve, reject) => {
    const GET_RECOMMENDATIONS = gql`
      query getRecommendedContributionAmounts(
        $groupId: ID
        $isSelfEmployed: Boolean
        $payrollFrequency: String
      ) {
        getRecommendedContributionAmounts(
          groupId: $groupId
          isSelfEmployed: $isSelfEmployed
          payrollFrequency: $payrollFrequency
        ) {
          daily {
            recommended
            other
            isMaxContributionOption
          }
          weekly {
            recommended
            other
            isMaxContributionOption
          }
          everyOtherWeek {
            recommended
            other
            isMaxContributionOption
          }
          monthly {
            recommended
            other
            isMaxContributionOption
          }
          perPayPeriod {
            recommended
            other
            isMaxContributionOption
          }
          dailyMax
          weeklyMax
          everyOtherWeekMax
          monthlyMax
          perPayPeriodMax
          scheduleFrequency
        }
      }
    `;
    const result = client.query<
      GetRecommendedContributionAmountsQuery,
      RecommendationVariables
    >({
      query: GET_RECOMMENDATIONS,
      variables: { groupId, isSelfEmployed, payrollFrequency },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getRecommendedContributionAmounts);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getContributionAbilityRequest(
  client: ApolloClient<object>
): Promise<GetContributionAbilityQuery["getContributionAbility"]> {
  return new Promise((resolve, reject) => {
    const query = gql`
      query getContributionAbility {
        getContributionAbility {
          currentYear {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          priorYear {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          history {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          isWithinPriorYearContributionWindow
          allYears {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
        }
      }
    `;
    const result = client.query<GetContributionAbilityQuery>({ query });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getContributionAbility);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

type ContributionEstimates = NonNullable<
  GetContributionAbilityAndEstimatesQuery["getState"]["user"]["contributionInfo"]
>["estimates"];

export function getContributionAbilityAndEstimatesRequest(
  client: ApolloClient<object>
): Promise<{
  estimates: ContributionEstimates | undefined;
  contributionAbility: GetContributionAbilityAndEstimatesQuery["getContributionAbility"];
}> {
  return new Promise((resolve, reject) => {
    const query = gql`
      query getContributionAbilityAndEstimates($rid: String!) {
        getContributionAbility {
          currentYear {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          priorYear {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          history {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
          isWithinPriorYearContributionWindow
          allYears {
            taxYear
            totalContributionAmount
            amountRemaining
            limit
            canContribute
          }
        }
        getState(rid: $rid) {
          user {
            contributionInfo {
              estimates {
                currentMonthAmount
                currentYearAmount
                currentMonthWithPendingAmount
                currentYearWithPendingAmount
              }
            }
          }
        }
      }
    `;
    const result = client.query<
      GetContributionAbilityAndEstimatesQuery,
      GetContributionAbilityAndEstimatesQueryVariables
    >({
      query,
      variables: { rid: "id-" + Math.random() },
    });
    result.then(
      (response) => {
        if (response.data) {
          const estimates = get(
            response.data,
            "getState.user.contributionInfo.estimates"
          );
          const contributionAbility = get(
            response.data,
            "getContributionAbility"
          );
          resolve({ estimates, contributionAbility });
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getDailyToRecurringAmount(
  client: ApolloClient<object>,
  { frequency, amount }: GetDailyToRecurringAmountQueryVariables
): Promise<GetDailyToRecurringAmountQuery["getDailyToRecurringAmount"]> {
  return new Promise((resolve, reject) => {
    const query = gql`
      query getDailyToRecurringAmount($frequency: String!, $amount: Float!) {
        getDailyToRecurringAmount(frequency: $frequency, amount: $amount)
      }
    `;
    const result = client.query<
      GetDailyToRecurringAmountQuery,
      GetDailyToRecurringAmountQueryVariables
    >({
      query,
      variables: {
        amount,
        frequency,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getDailyToRecurringAmount);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getRecurringContributions(
  client: ApolloClient<object>
): Promise<GetRecurringContributionsQuery["getRecurringContributions"]> {
  return new Promise((resolve, reject) => {
    const query = gql`
      query getRecurringContributions {
        getRecurringContributions {
          id
          employeeId
          contributionType
          amount
          bankId
          schedule {
            frequency
            anchorPayDate
            amount
            dayOne
            dayTwo
            sourceBankAccountId
            targetIraAccountId
            nextScheduledDate
          }
        }
      }
    `;
    const result = client.query<GetRecurringContributionsQuery>({
      query,
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getRecurringContributions);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// ---------------------------------------------------------------------------
// Paychex Flex (Perks) PAYCHECK_SPLIT operations.
// See PAYCHEX_PERKS_BACKEND_CONTRACT.md. Keyed off a worker record; the add/edit
// mutations return the resulting PaychexSplitContribution and delete returns a
// boolean. The dashboard card re-fetches getPaycheckSplitContribution after each
// mutation rather than reading these payloads.
// ---------------------------------------------------------------------------

export function submitOnboardingPaycheckSplitContribution(
  client: ApolloClient<object>,
  {
    workerRecordId,
    accountId,
    amount,
    isDailyCustom,
  }: AddPaycheckSplitContributionVariables
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const SUBMIT = gql`
      mutation addOnboardingPaycheckSplitContribution(
        $workerRecordId: ID!
        $accountId: ID!
        $amount: Float!
        $isDailyCustom: Boolean!
      ) {
        addOnboardingPaycheckSplitContribution(
          workerRecordId: $workerRecordId
          accountId: $accountId
          amount: $amount
          isDailyCustom: $isDailyCustom
        )
      }
    `;
    const result = client.mutate<{
      addOnboardingPaycheckSplitContribution: boolean;
    }>({
      mutation: SUBMIT,
      variables: { workerRecordId, accountId, amount, isDailyCustom },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addOnboardingPaycheckSplitContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function addPaycheckSplitContribution(
  client: ApolloClient<object>,
  {
    workerRecordId,
    accountId,
    amount,
    isDailyCustom,
  }: AddPaycheckSplitContributionVariables
): Promise<PaychexSplitContribution> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation addPaycheckSplitContribution(
        $contributionType: String
        $workerRecordId: ID!
        $accountId: ID!
        $amount: Float!
        $isDailyCustom: Boolean!
      ) {
        addPaycheckSplitContribution(
          contributionType: $contributionType
          workerRecordId: $workerRecordId
          accountId: $accountId
          amount: $amount
          isDailyCustom: $isDailyCustom
        ) {
          id
          amount
          accountId
          companyId
          frequency
          nextScheduledDate
          scheduleId
        }
      }
    `;
    const result = client.mutate<{
      addPaycheckSplitContribution: PaychexSplitContribution;
    }>({
      mutation: MUTATION,
      variables: {
        contributionType: contributionTypes.PAYCHECK_SPLIT,
        workerRecordId,
        accountId,
        amount,
        isDailyCustom,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.addPaycheckSplitContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function editPaycheckSplitContribution(
  client: ApolloClient<object>,
  {
    id,
    amount,
    isDailyCustom,
    accountId,
  }: EditPaycheckSplitContributionVariables
): Promise<PaychexSplitContribution> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation editPaycheckSplitContribution(
        $id: ID!
        $amount: Float!
        $isDailyCustom: Boolean!
        $accountId: ID!
      ) {
        editPaycheckSplitContribution(
          id: $id
          amount: $amount
          isDailyCustom: $isDailyCustom
          accountId: $accountId
        ) {
          id
          amount
          accountId
          companyId
          frequency
          nextScheduledDate
          scheduleId
        }
      }
    `;
    const result = client.mutate<{
      editPaycheckSplitContribution: PaychexSplitContribution;
    }>({
      mutation: MUTATION,
      variables: { id, amount, isDailyCustom, accountId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.editPaycheckSplitContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function deletePaycheckSplitContribution(
  client: ApolloClient<object>,
  { id }: { id: string }
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation deletePaycheckSplitContribution($id: ID!) {
        deletePaycheckSplitContribution(id: $id)
      }
    `;
    const result = client.mutate<{
      deletePaycheckSplitContribution: boolean;
    }>({
      mutation: MUTATION,
      variables: { id },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.deletePaycheckSplitContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export default {
  addContribution,
  addPaycheckContribution,
  editPaycheckContribution,
  getRecommendedContributionAmounts,
  getContributionAbilityRequest,
  getContributionAbilityAndEstimatesRequest,
  getDailyToRecurringAmount,
  addRecurringContribution,
  submitOnboardingPaycheckSplitContribution,
  addPaycheckSplitContribution,
  editPaycheckSplitContribution,
  deletePaycheckSplitContribution,
};
